import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ThumbsUp, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import AppImage from '../../../components/AppImage';

const ReviewCard = ({ review }) => {
  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Link
      to={`/reviews/${review?.id}`}
      className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100"
    >
      {/* Product Info Bar */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <AppImage
            src={review?.products?.imageUrl}
            alt={review?.products?.imageAlt || `${review?.products?.name} product image`}
            className="w-10 h-10 object-cover rounded"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {review?.products?.name}
            </p>
            <p className="text-xs text-gray-500">
              {review?.products?.brand}
            </p>
          </div>
        </div>
      </div>
      {/* Review Content */}
      <div className="p-5">
        {/* Reviewer Info */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <AppImage
              src={review?.avatarUrl}
              alt={review?.avatarAlt || `${review?.reviewerName} avatar`}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">
                  {review?.reviewerName}
                </h3>
                {review?.isVerified && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {renderStars(review?.rating)}
                <span className="text-xs text-gray-500">
                  {format(new Date(review?.createdAt), 'MMM dd, yyyy')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Review Title */}
        <h4 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {review?.title}
        </h4>

        {/* Review Content */}
        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
          {review?.content}
        </p>

        {/* Review Images Preview */}
        {review?.reviewImages?.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {review?.reviewImages?.slice(0, 3)?.map((image) => (
              <AppImage
                key={image?.id}
                src={image?.imageUrl}
                alt={image?.imageAlt || 'Review image'}
                className="w-20 h-20 object-cover rounded flex-shrink-0"
              />
            ))}
            {review?.reviewImages?.length > 3 && (
              <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-gray-600">
                  +{review?.reviewImages?.length - 3}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {review?.isAiGenerated && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              AI Generated
            </span>
          )}
          {review?.isVerified && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Verified Purchase
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-1 text-gray-500">
            <ThumbsUp className="w-4 h-4" />
            <span className="text-sm">
              {review?.helpfulCount} found helpful
            </span>
          </div>
          <span className="text-sm text-blue-600 font-medium hover:underline">
            Read full review →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ReviewCard;